import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { FaPlus } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import EmployeeSelect from '../../components/Formulaire/EmployeeSelect';
import { enveloppeDe } from '../../components/Utils/enveloppeDe';
import { notify } from '../../components/Utils/Notifications';
import { BOUTON_PRINCIPAL } from '../Employee/organigrammeOutils';
import { CLASSE_CHAMP } from './departOutils';
import { messageRefus } from './disciplineOutils';

/**
 * Le formulaire « Nouveau dossier » (C39 `dossiers/creer`), en place dans la
 * carte. Avec `employeId` (fiche du salarié), le salarié est figé. La date des
 * faits n'est PAS bornée : Odoo accepte une date future, le portail n'est pas
 * plus strict. Un refus s'affiche par `notify` et garde la saisie.
 */
function NouveauDossierDisciplinaire({ employeId, onCree }) {
  const { t } = useTranslation();
  const [ouvert, setOuvert] = useState(false);
  const [salarie, setSalarie] = useState(null);
  const [dateFaits, setDateFaits] = useState('');
  const [faits, setFaits] = useState('');
  const [envoi, setEnvoi] = useState(false);
  // Garde synchrone : deux submit dans le même tick passent avant tout re-rendu.
  const enVol = useRef(false);

  const idSalarie = employeId ?? salarie?.value;
  // Même règle qu'Odoo (`_texte`) : des faits d'espaces sont des faits absents.
  const complet = Boolean(idSalarie && dateFaits && faits.trim());

  const fermer = () => {
    setOuvert(false);
    setSalarie(null);
    setDateFaits('');
    setFaits('');
  };

  const creer = async (evenement) => {
    evenement.preventDefault();
    if (enVol.current || !complet) return;
    enVol.current = true;
    setEnvoi(true);
    const reponse = await enveloppeDe(() => apiService.creerDossierDisciplinaire(idSalarie, dateFaits, faits));
    enVol.current = false;
    setEnvoi(false);
    if (reponse.ok) {
      notify(t('discipline.cree'), '', 2);
      fermer();
      onCree(reponse.dossier);
    } else {
      // Le refus `non_autorise` de la création a son propre libellé (son propre nom).
      notify(messageRefus(reponse, t, 'creer'), '', 3);
    }
  };

  if (!ouvert) {
    return (
      <button type="button" data-testid="dossier-nouveau" className={BOUTON_PRINCIPAL} onClick={() => setOuvert(true)}>
        <FaPlus size={12} /> {t('discipline.nouveau')}
      </button>
    );
  }

  return (
    <form data-testid="dossier-formulaire" onSubmit={creer} className="space-y-4">
      <h2 className="text-base sm:text-lg font-semibold">{t('discipline.nouveau')}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {!employeId && (
          <div className="text-sm font-medium">
            <span>{t('discipline.employe')}</span>
            <div className="mt-1"><EmployeeSelect value={salarie} onChange={setSalarie} aria-label={t('discipline.employe')} /></div>
          </div>
        )}
        <label className="block text-sm font-medium">
          {t('discipline.dateFaits')}
          <input type="date" data-testid="dossier-date-faits" value={dateFaits}
            onChange={(e) => setDateFaits(e.target.value)} className={CLASSE_CHAMP} />
        </label>
      </div>
      <label className="block text-sm font-medium">
        {t('discipline.faits')}
        <textarea data-testid="dossier-faits" rows={4} value={faits}
          onChange={(e) => setFaits(e.target.value)} className={CLASSE_CHAMP} />
      </label>
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button type="button" className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg" onClick={fermer}>
          {t('discipline.retour')}
        </button>
        <button type="submit" data-testid="dossier-creer" disabled={!complet || envoi} className={BOUTON_PRINCIPAL}>
          {envoi ? t('discipline.envoi') : t('discipline.creer')}
        </button>
      </div>
    </form>
  );
}

NouveauDossierDisciplinaire.propTypes = { employeId: PropTypes.number, onCree: PropTypes.func.isRequired };

export default NouveauDossierDisciplinaire;
